import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";

const RABBI_IMG = "/rabbi-karen.jpg";
const RABBI_FALLBACK = "/wp-content/uploads/2025/01/karen-269x300.jpg";

export default function Rabbi() {
  return (
    <>
      <SEOHead
        title="Meet Rabbi Karen Fanwick - Reform Synagogue Melbourne FL"
        description="Meet Rabbi Karen Fanwick, spiritual leader of Temple Israel of Brevard - the Reform synagogue in Melbourne, FL. Author of Hebrew 101 and Hebrew 102, teacher of A Journey of Discovery for those becoming Jewish."
        path="/rabbi"
      />
      <PageHero
        eyebrow="Meet Our Rabbi"
        title="Rabbi Karen Fanwick"
        subtitle="A teacher, a learner, and a warm spiritual guide for our congregation."
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-3 gap-12 items-start">
          <aside className="lg:col-span-1 lg:sticky lg:top-28">
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-3 bg-[var(--blue)]/10 rounded-3xl rotate-[-2deg]" />
              <img
                src={RABBI_IMG}
                onError={(e) => { e.currentTarget.src = RABBI_FALLBACK; }}
                alt="Rabbi Karen Fanwick"
                className="relative rounded-3xl w-full shadow-xl object-cover aspect-[3/4]"
              />
            </div>
            <div className="mt-6 card-soft p-6 bg-[var(--cream)] border-[var(--line-warm)]">
              <p className="eyebrow mb-2">★ Contact Rabbi Fanwick</p>
              <a href="tel:+13216319494" className="block text-[var(--blue-ink)] font-medium">(321) 631-9494</a>
              <a href="mailto:rabbikarenfanwick@tiofbrevard.com" className="block text-[var(--blue-deep)] text-sm break-all hover:text-[var(--blue)] mt-1">
                rabbikarenfanwick@tiofbrevard.com
              </a>
            </div>
          </aside>

          <div className="lg:col-span-2 prose-tiob">
            <p className="font-display text-2xl text-[var(--blue-ink)] leading-snug not-italic">
              <em>“From my experience of learning Hebrew as an older adult, I realized that most
              programs designed for adults include far too much extraneous information.
              Determined to find a better approach, I developed methods to facilitate adult
              Hebrew education.”</em>
            </p>
            <p>
              Thirty-five years ago, I began my spiritual journey. It started when I met the man
              who would later become my husband. On our very first date, I discovered he was Jewish.
              I had been pondering Judaism for some time but had never taken the step of speaking
              with a rabbi or beginning that journey.
            </p>
            <p>
              After Phil and I married, I began the process of converting from something I really
              wasn't to something I longed to be. Once I completed my conversion, I began singing
              for the temple we belonged to and became the cantorial soloist. I immersed myself in
              Jewish music, attending as many workshops as possible to learn and grow. I taught music
              in the religious school and, after learning Hebrew, taught it to children in my
              congregation. I also taught adults.
            </p>
            <p>
              Fast forward to 2013. I had been exploring the possibility of attending rabbinic
              school at various institutions, but no seminary fit my schedule. Unable to relocate
              for school, I stumbled upon a remote learning opportunity that allowed me to study
              with individuals worldwide to become a rabbi! The program, called the Jewish Spiritual
              Leaders Institute, accepted my application, and after completing the program, I became
              an ordained rabbi specializing in working with smaller congregations. One of my main
              interests is helping people discover their spiritual roots through the process of
              conversion. My program is called <em>A Journey of Discovery</em>.
            </p>
            <p>
              After serving Temple Israel in West Lafayette, IN, for two years as the rabbi in
              residence, I retired, thinking my rabbinic days were behind me. They were not.
              In the spring of 2023, I applied for the position of rabbi at Temple Israel of
              Brevard in Melbourne, FL, and was hired to lead this wonderful group of people as
              their spiritual leader.
            </p>
            <p>
              My approach to Hebrew education resulted in the publication of two books:
              <em> Hebrew 101 - Beginning Biblical Hebrew for Adults</em> and{" "}
              <em>Hebrew 102 - Beginning Hebrew Prayer for Adults</em>. I use these texts in my
              classes and find that most adults succeed in their learning, which is the ultimate
              goal of education! My next project will be creating a Hebrew curriculum for children.
            </p>
            <p>
              I am originally from Indiana, where I lived for many years with my husband, Phil.
              We raised three wonderful children, now adults, who still reside in Indiana.
              We continue to own a hobby farm with 10 acres of land. At one point, we had three
              horses, a dog, two cats, and three turtles! In my spare time, I enjoy quilting,
              playing bridge, birdwatching, and following sports.
            </p>
            <p>
              I feel truly blessed to have found this congregation and am working diligently
              to help them heal, move forward, and become the best they can be.
            </p>
            <p className="font-display text-xl text-[var(--blue-ink)] mt-8">- Rabbi Karen Fanwick</p>

            <blockquote className="border-l-4 border-[var(--blue)] pl-5 italic text-[var(--ink-soft)] mt-8 bg-[var(--cream)] rounded-r-2xl py-4 pr-5">
              "Find yourself a rabbi, acquire for yourself a friend, and judge every person on
              the positive side." <span className="not-italic text-sm text-[var(--muted)]">- Pirke Avot 1:6</span>
            </blockquote>

            <div className="mt-10 flex flex-wrap gap-4 not-prose">
              <Link to="/contact" className="btn-primary" data-testid="rabbi-contact-cta">
                Schedule a conversation
              </Link>
              <Link to="/worship" className="btn-ghost">Worship with us</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
